import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DeletionRequestsTable } from '@/components/deletion-requests/DeletionRequestsTable';
import {
  DeletionRequestFilters,
  type DeletionRequestFiltersState,
} from '@/components/deletion-requests/DeletionRequestFilters';
import { apiClient } from '@/services/api-client';
import type { DeletionRequest, PaginatedResponse } from '@/types';

export function DeletionRequestsPage() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<DeletionRequestFiltersState>({});

  const {
    data: requestsData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['admin', 'deletion-requests', page, filters],
    queryFn: async () => {
      const params: Record<string, string> = {
        page: String(page),
        limit: '10',
      };
      if (filters.search) params.search = filters.search;
      if (filters.status) params.status = filters.status;

      const response = await apiClient.get<PaginatedResponse<DeletionRequest>>(
        '/api/v1/admin/deletion-requests',
        params
      );
      if (!response.success) throw new Error(response.error?.message);

      return {
        items: (response.data as unknown as DeletionRequest[]) || [],
        meta: response.meta || { page: 1, limit: 10, total: 0, totalPages: 0 },
      } as PaginatedResponse<DeletionRequest>;
    },
  });

  // Clamp the page if a mutation (archive/delete/restore) shrinks the result
  // set below the current page — otherwise the table shows an empty page while
  // earlier pages still have data.
  useEffect(() => {
    const totalPages = requestsData?.meta.totalPages ?? 0;
    if (totalPages > 0 && page > totalPages) setPage(totalPages);
  }, [requestsData, page]);

  const handleFiltersChange = (newFilters: DeletionRequestFiltersState) => {
    setFilters(newFilters);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-display text-3xl font-bold text-foreground">Deletion Requests</h1>
        <p className="text-muted-foreground">
          Archive a request first to disable the account; permanently delete only later, after a
          hold period. Archived accounts can be restored.
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="font-display">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <DeletionRequestFilters filters={filters} onFiltersChange={handleFiltersChange} />
        </CardContent>
      </Card>

      {/* Requests Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-display">All Requests</CardTitle>
        </CardHeader>
        <CardContent>
          <DeletionRequestsTable
            data={requestsData || null}
            isLoading={isLoading}
            page={page}
            onPageChange={setPage}
            onRefresh={() => refetch()}
          />
        </CardContent>
      </Card>
    </div>
  );
}
